/** Each athlete's weekly lifting load, read against their own usual (DESIGN §16.5). */

export interface LoadData {
  /** Mondays, oldest first; the last is the week shown as "this week". */
  weeks: string[];
  athletes: { id: string; since: string | null; loads: number[] }[];
}

export type LoadTrend = "high" | "low" | "usual" | "early" | "new" | "none";

export interface LoadStatus {
  value: number;
  /** The mean of the four weeks before, from their first logged week; null without two of them. */
  usual: number | null;
  /** Percent above (+) or below (−) their usual. */
  change: number | null;
  trend: LoadTrend;
}

export const HIGH = 1.3;
export const LOW = 0.7;

/**
 * `ongoing` is the week still running: it only grows, so it never reads as below their usual
 * until it is over.
 */
export function loadStatus(weeks: string[], athlete: { since: string | null; loads: number[] }, index = weeks.length - 1, ongoing = false): LoadStatus {
  const value = athlete.loads[index] ?? 0;
  if (!athlete.since) return { value, usual: null, change: null, trend: "none" };
  const before: number[] = [];
  for (let i = Math.max(0, index - 4); i < index; i++) if (weeks[i]! >= athlete.since) before.push(athlete.loads[i] ?? 0);
  if (before.length < 2) return { value, usual: null, change: null, trend: "new" };
  const usual = before.reduce((a, b) => a + b, 0) / before.length;
  if (usual <= 0) return { value, usual, change: null, trend: value > 0 ? "high" : "usual" };
  const ratio = value / usual;
  const change = Math.round((ratio - 1) * 100);
  const trend: LoadTrend = ratio >= HIGH ? "high" : ratio <= LOW ? (ongoing ? "early" : "low") : "usual";
  return { value, usual, change, trend };
}

export function trendText(s: LoadStatus): string {
  switch (s.trend) {
    case "high":
      return s.change != null ? `Well above their usual (+${s.change}%)` : "Well above their usual";
    case "low":
      return `Well below their usual (${s.change}%)`;
    case "usual":
      return "About their usual";
    case "early":
      return "So far this week";
    case "new":
      return "Not enough weeks yet to compare";
    case "none":
      return "No weighed sets shared yet";
  }
}

/** A few words for a list row: "↑ High load". */
export function trendShort(s: LoadStatus): string | null {
  return s.trend === "high" ? "High load" : s.trend === "low" ? "Low load" : null;
}

export function loadNumber(n: number): string {
  return n >= 100 ? String(Math.round(n)) : n.toFixed(1).replace(/\.0$/, "");
}
